/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-06-06 20:09:47
 * @ Description: A table with comps for list view
 */

import React, { useState } from 'react'
import {
  strategyScrollXTouchSx,
  strategyRelPosStyle,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import Pagination from '@mui/material/Pagination'
import { formatDateYmd } from '@/utils/date'

// Centralized lodash imports - much cleaner!
import {
  map,
  split,
  ceil,
  slice,
  head,
  get,
  isString,
  every,
  isNil,
  includes,
  intersection,
  compact,
  isEmpty,
} from '@/utils/lodash'

import {
  Grid,
  Avatar,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  List,
  ListItemButton,
  Menu,
  Table,
  TableCell,
  TableSortLabel,
  Tooltip,
  Box,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import useSorting, {
  getSorting,
  stableSort,
} from '@/hooks/useSorting'

import { compMarkerColor } from '@/housefolios-components/PropertyContainer/CenterPage/compMarkerColors'

function CenterPageCompListComponent(props) {
  const { comps, property, openEdit } = props

  const activeOrg = property?.portfolio?.organization

  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedComp, setSelectedComp] = useState('')
  const handleOpenMoreActions = (event, _id) => {
    setAnchorEl(event.currentTarget)
    setSelectedComp(_id)
  }
  const handleCloseMoreActions = () => {
    setAnchorEl(null)
    setSelectedComp('')
  }

  const [defaultOrderByTarget = 'saleDate', defaultOrder = 'desc'] =
    split(props.ordering || 'saleDate-desc', '-')

  const [{ order, orderBy }, { handleRequestSort }] = useSorting({
    defaultOrder,
    defaultOrderBy: defaultOrderByTarget,
  })

  // sets page in local storage for potential comps if it recieved a title. Need to change if we end up with more comp lists
  const [page, setPage] = useState(
    parseInt(
      localStorage.getItem(
        props.title
          ? `${property._id}-comps-potential-list-page`
          : `${property._id}-comps-actual-list-page`,
      ),
    ) || 1,
  )
  const handleChange = (event, value) => {
    if (props.title)
      localStorage.setItem(
        `${property._id}-comps-potential-list-page`,
        value,
      )
    else
      localStorage.setItem(
        `${property._id}-comps-actual-list-page`,
        value,
      )
    setPage(value)
  }

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })

  const tableHeaders = [
    { title: 'Address', name: 'address' },
    {
      title: 'Status',
      name: 'status',
      formatter: (x) => {
        if (!x) return ''
        else {
          return (
            <Chip
              label={x} sx={{
                backgroundColor:
                  x === 'Sold'
                    ? '#f83245'
                    : x === 'Active'
                      ? '#1bc943'
                      : x === 'Pending'
                        ? '#f4772e'
                        : null,
              }}/>
          )
        }
      },
    },
    { title: 'City', name: 'city' },
    { title: 'State', name: 'state' },
    { title: 'Zip', name: 'zip' },
    { title: 'Beds', name: 'beds' },
    { title: 'Baths', name: 'baths' },
    {
      title: 'SqFt',
      name: 'sqft',
      formatter: (x) => numberFormat.format(x),
    },
    { title: 'Year', name: 'year' },
    {
      title: 'Sold Price',
      name: 'estval',
      formatter: (x) => currencyFormat.format(x),
    },
    {
      title: 'Date Sold',
      name: 'saleDate',
      formatter: (x) => formatDateYmd(x),
    },
    {
      title: 'Distance Away',
      name: 'distance',
      formatter: (x) => numberFormat.format(x),
    },
    {
      title: 'Days on Market (D.O.M)',
      name: 'daysOnMarket',
      formatter: (x) => {
        if (!isNil(x)) return numberFormat.format(x)
        else return '-'
      },
    },
    { title: 'Rehab Level', name: 'rehabLevel' },
    {
      title: 'Price Adjustment',
      name: 'priceAdjustment',
      formatter: (x) => Number(x) + '%',
    },
    {
      title: 'Date Added',
      name: 'createdAt',
      formatter: (x) => formatDateYmd(x),
    },
    { title: 'Added By', name: 'createdBy.username' },
  ]

  const compare = intersection(
    map(comps, (comp) => comp._id),
    props.selected,
  )

  const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  }

  const count = ceil(comps.length / 25)
  if (page > count) setPage(count || 1)
  return (
    <Grid size={12} sx={[{ minWidth: 0, maxWidth: '100%', width: '100%' }, { marginBottom: "24px" }]}>
      {/* minWidth: 0 + overflow hidden keeps the table scrolling inside the card */}
      <Card sx={[{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }, { marginBottom: "48px" }]}>
        <Box sx={[{
          display: "flex",
          alignItems: "center",
          py: "0.75rem",
          px: "1.25rem",
          mb: 0,
          backgroundColor: "#fff",
          padding: "0.75rem 1.25rem",
          marginBottom: 0,
          borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
          "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
          "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
        }, { pr: '0.5rem' }]}>
          <Box sx={{
            flexGrow: 1,
            minWidth: 0,
            "& > b": { display: "block" },
            "& > small": {
              textTransform: "uppercase",
              display: "block",
              opacity: 0.3,
              fontWeight: "bold",
            },
          }}>
            <small>{property.address}</small>
            <b>{props.title ? props.title : 'Comps'}</b>
          </Box>
        </Box>
        <CardContent sx={{ p: 0, minWidth: 0 }}>
          <Box sx={[strategyScrollXTouchSx, { pb: 0.5 }]}>
            <Table sx={[{ whiteSpace: 'nowrap', mb: 0 }, {
              width: "100%",
              marginBottom: "1rem",
              fontSize: "1rem",
              "& th, & td": { padding: "0.75rem", borderTop: "1px solid #e4e5f0", verticalAlign: "middle" },
              "& thead th": {
                textTransform: "uppercase",
                background: "#f4f5fd",
                fontSize: "0.875rem",
                verticalAlign: "middle",
                borderBottom: "2px solid #e4e5f0",
              },
              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
            }]}>
              <thead>
                <tr sx={{ textAlign: 'center' }}>
                  {!props.shared && (
                    <TableCell>
                      {/* <Tooltip title="Select All">
                        <Checkbox
                          color="primary"
                          id="CustomCheckbox3"
                          checked={compare.length > 0}
                          indeterminate={
                            compare.length < comps.length &&
                            compare.length > 0
                          }
                          onClick={props.selectAll}
                        />
                      </Tooltip> */}
                    </TableCell>
                  )}
                  <TableCell
                    key={'Address'}
                    //style={{ whiteSpace: 'nowrap' }}
                    sortDirection={
                      orderBy === 'address' ? order : false
                    } sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={orderBy === 'address'}
                      direction={order}
                      onClick={(event) =>
                        handleRequestSort(event, 'address')
                      }
                    >
                      {'Address'}
                      {orderBy === 'address' ? (
                        <span style={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Google Search</TableCell>
                  {map(slice(tableHeaders, 1), (column) => (
                    <TableCell
                      key={column.title}
                      //style={{ whiteSpace: 'nowrap' }}
                      align={column.align}
                      sortDirection={
                        orderBy === column.name ? order : false
                      } sx={{ whiteSpace: 'nowrap' }}>
                      {column.name ? (
                        <TableSortLabel
                          active={orderBy === column.name}
                          direction={order}
                          onClick={(event) =>
                            handleRequestSort(event, column.name)
                          }
                        >
                          {column.title}
                          {orderBy === column.name ? (
                            <span style={visuallyHidden}>
                              {order === 'desc'
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </span>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        column.title
                      )}
                    </TableCell>
                  ))}
                  {!props.shared && <TableCell>Action</TableCell>}
                </tr>
              </thead>
              <tbody>
                {map(
                  slice(
                    stableSort(comps, getSorting(order, orderBy)),
                    (page - 1) * 25,
                    page * 25,
                  ),
                  (comp, index) => (
                    <tr
                      key={comp._id || `comp-row-${index}`}
                      style={strategyRelPosStyle}
                    >
                      {!props.shared && (
                        <td sx={{ textAlign: 'center' }}>
                          <Checkbox
                            color="primary"
                            id="CustomCheckbox4"
                            checked={includes(
                            props.selected,
                            comp._id,
                            )}
                            onClick={props.selectComp(comp._id)}
                            sx={{ alignSelf: 'flex-start' }}
                            />
                        </td>
                      )}
                      <td>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Avatar sx={{
                              height: 24,
                              width: 24,
                              fontSize: 12,
                              backgroundColor: compMarkerColor(
                                comp.potential,
                              ),
                              color: '#fff',
                              marginRight: '5px',
                            }}>
                            {comp.mapIndex + 1}
                          </Avatar>
                          <Box
                            sx={{
                              mr: '0.5rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {!isEmpty(compact(comp.images)) ? (
                              <Box sx={[{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 44,
                                height: 44,
                                lineHeight: 1,
                                borderRadius: 50,
                                boxShadow: "0 0 0 3px #fff",
                                overflow: "hidden",
                                "& img": { width: "100%", height: "100%", display: "block" },
                                "& svg, & .svg-inline--fa": { display: "block" },
                                transition: "all 0.2s ease-in-out",
                                opacity: 1,
                                "&.rounded": { borderRadius: "0.65rem !important" },
                                "html[data-color-mode=\"dark\"] &": { boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.2)" },
                              }, { borderRadius: '0.65rem', display: 'flex', justifyContent: 'center', alignItems: 'center', width: 32, height: 32, lineHeight: '32px' }]}>
                                <Box sx={[{ backgroundImage: 'url(' + head(comp.images).url_small + ')', }, { backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" }]}>
                                  <img
                                    src={head(comp.images).url}
                                    loading="lazy"
                                  />
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={[{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 44,
                                height: 44,
                                lineHeight: 1,
                                borderRadius: 50,
                                boxShadow: "0 0 0 3px #fff",
                                overflow: "hidden",
                                "& img": { width: "100%", height: "100%", display: "block" },
                                "& svg, & .svg-inline--fa": { display: "block" },
                                transition: "all 0.2s ease-in-out",
                                opacity: 1,
                                "&.rounded": { borderRadius: "0.65rem !important" },
                                "html[data-color-mode=\"dark\"] &": { boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.2)" },
                              }, { borderRadius: '0.65rem', width: 32, height: 32, lineHeight: '32px' }]}>
                                <img
                                  src="/images/default_image.png"
                                  alt="..."
                                />
                              </Box>
                            )}
                          </Box>
                          <div>
                            <Box component="a" href="/" onClick={(e) => { e.preventDefault(); openEdit(comp) }} title="..." sx={[{ fontWeight: 700 }, {
                              color: "#070919",
                              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                            }]}>
                              {comp.address}
                            </Box>
                          </div>
                        </Box>
                      </td>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            window.open(
                              `https://www.google.com/search?q=${comp?.address} ${comp?.city}, ${comp?.state}`,
                              '_blank',
                              // 'toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=400,height=400',
                            )
                          }}
                        >
                          <FontAwesomeIcon
                            icon={['fab', 'google']}
                          />
                        </IconButton>
                      </TableCell>
                      {map(slice(tableHeaders, 1), (column) => (
                        <td key={column.name}>
                          <div>
                            {column.formatter
                              ? column.formatter(
                                  get(comp, column.name),
                                )
                              : get(comp, column.name)}
                          </div>
                        </td>
                      ))}
                      {!props.shared && (
                        <td>
                          <div>
                            <Tooltip title="More Actions">
                              <IconButton
                                onClick={(event) =>
                                  handleOpenMoreActions(
                                    event,
                                    comp._id,
                                  )
                                }
                               
                                size="large" sx={{
                                  backgroundColor:
                                    'rgba(54, 140, 255, 0.22)',
                                  color: '#90caf9',
                                  height: 30,
                                  width: 30,
                                }}>
                                <FontAwesomeIcon icon={['fas', 'ellipsis']} sx={{ fontSize: "1.15rem !important" }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                          <Menu
                            anchorEl={anchorEl}
                            keepMounted
                            open={
                              Boolean(anchorEl) &&
                              comp._id === selectedComp
                            }
                            onClose={handleCloseMoreActions}
                            slotProps={{ list: { sx: { p: 0 } } }}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'center',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'center',
                            }}
                          >
                            <div>
                              <List
                                component="div"
                                sx={{
                                  "& .MuiListItem-root.MuiListItem-button": { borderRadius: 0 },
                                  "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItem-root.MuiListItem-button:hover, & .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root:hover": {
                                    color: "#368cff",
                                    background: "rgba(54, 140, 255, 0.05)",
                                    "&::before": { background: "#368cff" },
                                  },
                                }}
                              >
                                <ListItemButton
                                  onClick={() => {
                                    openEdit(comp)
                                    handleCloseMoreActions()
                                  }}
                                >
                                  <span>Edit Comp</span>
                                </ListItemButton>
                              </List>
                            </div>
                          </Menu>
                        </td>
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </Table>
          </Box>
          <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
          <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
          {count && (
            <Box
              sx={{
                p: '1rem',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Pagination size="small" count={count} page={page} onChange={handleChange} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  )
}

export default withSession(CenterPageCompListComponent)
